import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Form,
  Select,
  Skeleton,
  Space,
  Spin,
  Switch,
} from 'antd';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  ebayMarketplaceLoad,
  ebayMarketplaceLoadFulfillmentPolicies,
  ebayMarketplaceLoadPaymentPolicies,
  ebayMarketplaceLoadReturnPolicies,
  ebayMarketplaceSetCurrency,
  ebayMarketplaceSetIsActive,
  ebayMarketplaceSetLanguage,
  ebayMarketplacReloadCategories,
  ebayMarketplacReloadDefaultCategoryId,
  ebayMarketplacReloadFulfillmentPolicies,
  ebayMarketplacReloadPaymentPolicies,
  ebayMarketplacReloadReturnPolicies,
} from '../reducers/ebay/reducer';
import { SPACING } from '../theme/tokens';

function getPolicyData(policies: { name: string }[] | undefined): {
  names: string;
  loading: boolean;
} {
  if (!policies) {
    return { names: '', loading: true };
  }
  return {
    names: policies.map(({ name }) => name).join(', ') || EMPTY_VALUE,
    loading: false,
  };
}

function EbayMarketplaceContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const marketplace = useSelector((s: RootState) => s.Ebay.marketplace);
  const ebayFulfillmentPolicies = useSelector(
    (s: RootState) => s.Ebay.fulfillmentPolicies,
  );
  const ebayPaymentPolicies = useSelector(
    (s: RootState) => s.Ebay.paymentPolicies,
  );
  const ebayReturnPolicies = useSelector(
    (s: RootState) => s.Ebay.returnPolicies,
  );
  const ebayInProcess = useSelector((s: RootState) => s.Ebay.inProcess);
  const languageLanguages = useSelector((s: RootState) => s.Language.languages);
  const currencyCurrencies = useSelector(
    (s: RootState) => s.Currency.currencies,
  );

  const { code } = useParams();

  useEffect(() => {
    if (code) {
      dispatch(ebayMarketplaceLoad(code));
      dispatch(ebayMarketplaceLoadFulfillmentPolicies(code));
      dispatch(ebayMarketplaceLoadPaymentPolicies(code));
      dispatch(ebayMarketplaceLoadReturnPolicies(code));
    }
  }, [dispatch, code]);

  const fulfillmentData = useMemo(
    () =>
      getPolicyData(
        marketplace?.code
          ? ebayFulfillmentPolicies[marketplace.code]
          : undefined,
      ),
    [ebayFulfillmentPolicies, marketplace?.code],
  );

  const paymentData = useMemo(
    () =>
      getPolicyData(
        marketplace?.code ? ebayPaymentPolicies[marketplace.code] : undefined,
      ),
    [ebayPaymentPolicies, marketplace?.code],
  );

  const returnData = useMemo(
    () =>
      getPolicyData(
        marketplace?.code ? ebayReturnPolicies[marketplace.code] : undefined,
      ),
    [ebayReturnPolicies, marketplace?.code],
  );

  if (!marketplace) {
    return <Skeleton active />;
  }

  const { names: fulfillmentsNames, loading: fulfillmentsPoliciesLoading } =
    fulfillmentData;
  const { names: paymentsNames, loading: paymentsPoliciesLoading } =
    paymentData;
  const { names: returnsNames, loading: returnsPoliciesLoading } = returnData;

  const pageHeader: string = `Ebay - ${marketplace.name}`;

  const onChange = (isActive: boolean): void => {
    dispatch(ebayMarketplaceSetIsActive(marketplace.code, isActive));
  };

  const onFinishLanguage = (values: { languageTypeCode: string }) =>
    dispatch(
      ebayMarketplaceSetLanguage(marketplace.code, values.languageTypeCode),
    );

  const onFinishCurrency = (values: { currencyIsoCode: string }) =>
    dispatch(
      ebayMarketplaceSetCurrency(marketplace.code, values.currencyIsoCode),
    );

  return (
    <>
      <DetailPageHeader
        title={pageHeader}
        onReload={(): void => {
          dispatch(ebayMarketplaceLoad(marketplace.code));
          dispatch(ebayMarketplaceLoadFulfillmentPolicies(marketplace.code));
          dispatch(ebayMarketplaceLoadPaymentPolicies(marketplace.code));
          dispatch(ebayMarketplaceLoadReturnPolicies(marketplace.code));
        }}
        isReloadDisabled={ebayInProcess}
      />

      <Descriptions
        title="Ebay marketplace"
        bordered
        column={{ xs: 1, sm: 1, md: 1 }}
        items={[
          {
            key: 'name',
            label: 'Name',
            span: 1,
            children: <strong>{marketplace.name}</strong>,
          },
          {
            key: 'code',
            label: 'Code',
            span: 1,
            children: <strong>{marketplace.code}</strong>,
          },
          {
            key: 'language',
            label: 'Language',
            span: 1,
            children: <strong>{marketplace.ebayLanguage}</strong>,
          },
          {
            key: 'is-active',
            label: 'Is active',
            span: 1,
            children: (
              <Switch
                disabled={ebayInProcess}
                defaultChecked={marketplace.isActive}
                onChange={onChange}
              />
            ),
          },
          {
            key: 'default-category-id',
            label: 'Default category ID',
            span: 1,
            children: (
              <Space size={SPACING.lg} align="start">
                <strong>{marketplace.defaultCategoryId || EMPTY_VALUE}</strong>
                <Button
                  color="primary"
                  onClick={() => {
                    dispatch(
                      ebayMarketplacReloadDefaultCategoryId(marketplace.code),
                    );
                  }}
                  disabled={ebayInProcess}
                >
                  <ReloadOutlined />
                </Button>
              </Space>
            ),
          },
          {
            key: 'categories-count',
            label: 'Categories count',
            span: 1,
            children: (
              <Space size={SPACING.lg} align="start">
                <strong>{marketplace.categoriesCount}</strong>
                <Button
                  color="primary"
                  onClick={() => {
                    if (marketplace.defaultCategoryId) {
                      dispatch(
                        ebayMarketplacReloadCategories(
                          marketplace.code,
                          marketplace.defaultCategoryId,
                        ),
                      );
                    }
                  }}
                  disabled={ebayInProcess || !marketplace.defaultCategoryId}
                >
                  <ReloadOutlined />
                </Button>
              </Space>
            ),
          },
          {
            key: 'language-2',
            label: 'Language',
            span: 1,
            children: (
              <Form
                onFinish={onFinishLanguage}
                initialValues={{
                  languageTypeCode: marketplace.languageType.code,
                }}
              >
                <Space size={SPACING.lg} align="start">
                  <Form.Item name="languageTypeCode">
                    <Select
                      style={{ minWidth: '10rem' }}
                      options={languageLanguages?.map((language) => ({
                        value: language.code,
                        label: `${language.name} (${language.code})`,
                      }))}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={ebayInProcess}
                    loading={ebayInProcess}
                  >
                    Save
                  </Button>
                </Space>
              </Form>
            ),
          },
          {
            key: 'currency',
            label: 'Currency',
            span: 1,
            children: (
              <Form
                onFinish={onFinishCurrency}
                initialValues={{
                  currencyIsoCode: marketplace.currencyType.isoCode,
                }}
              >
                <Space size={SPACING.lg} align="start">
                  <Form.Item name="currencyIsoCode">
                    <Select
                      style={{ minWidth: '10rem' }}
                      options={currencyCurrencies.map((currency) => ({
                        value: currency.isoCode,
                        label: `${currency.name} (${currency.isoCode})`,
                      }))}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={ebayInProcess}
                    loading={ebayInProcess}
                  >
                    Save
                  </Button>
                </Space>
              </Form>
            ),
          },
          {
            key: 'fulfillment',
            label: 'Fulfillment',
            span: 1,
            children: (
              <Space size={SPACING.lg} align="start">
                {fulfillmentsPoliciesLoading ? (
                  <Spin size="small" />
                ) : (
                  <strong>{fulfillmentsNames}</strong>
                )}
                <Button
                  color="primary"
                  onClick={() => {
                    dispatch(
                      ebayMarketplacReloadFulfillmentPolicies(marketplace.code),
                    );
                  }}
                  disabled={ebayInProcess}
                >
                  <ReloadOutlined />
                </Button>
              </Space>
            ),
          },
          {
            key: 'payment',
            label: 'Payment',
            span: 1,
            children: (
              <Space size={SPACING.lg} align="start">
                {paymentsPoliciesLoading ? (
                  <Spin size="small" />
                ) : (
                  <strong>{paymentsNames}</strong>
                )}
                <Button
                  color="primary"
                  onClick={() => {
                    dispatch(
                      ebayMarketplacReloadPaymentPolicies(marketplace.code),
                    );
                  }}
                  disabled={ebayInProcess}
                >
                  <ReloadOutlined />
                </Button>
              </Space>
            ),
          },
          {
            key: 'return',
            label: 'Return',
            span: 1,
            children: (
              <Space size={SPACING.lg} align="start">
                {returnsPoliciesLoading ? (
                  <Spin size="small" />
                ) : (
                  <strong>{returnsNames}</strong>
                )}
                <Button
                  color="primary"
                  onClick={() => {
                    dispatch(
                      ebayMarketplacReloadReturnPolicies(marketplace.code),
                    );
                  }}
                  disabled={ebayInProcess}
                >
                  <ReloadOutlined />
                </Button>
              </Space>
            ),
          },
        ]}
      />
    </>
  );
}

export default EbayMarketplaceContainer;
